<script setup lang="ts">
import {computed, ref, watch} from "vue";
import {useRouter} from "vue-router";
import {useNow} from "@vueuse/core";
import {ArrowRight, Lock, Smartphone, Sparkles, TriangleAlert, X} from "@lucide/vue";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from "@/core/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from "@/core/components/ui/select";
import {Button} from "@/core/components/ui/button";
import {Input} from "@/core/components/ui/input";
import {Label} from "@/core/components/ui/label";
import {Textarea} from "@/core/components/ui/textarea";
import useAgendaOperators from "@/agenda/composables/useAgendaOperators";
import useOperatorChoices from "@/agenda/composables/useOperatorChoices";
import HabilitatedBadge from "@/agenda/components/HabilitatedBadge.vue";
import {accessIdForAppointment, isUnassigned} from "@/agenda/lib/operators";
import {AgendaAppointment} from "@/core/interfaces/AgendaAppointment";
import {AppointmentCreateRequest} from "@/core/http/resources/AppointmentResource";
import useAppointmentTypes from "@/agenda/composables/useAppointmentTypes";
import AgendaPatientSearch, {SelectedPatient} from "@/agenda/components/AgendaPatientSearch.vue";
import DurationStepper from "@/agenda/components/DurationStepper.vue";
import {defaultSlotForDay, earliestStart, isPastStart, parseAgendaDate} from "@/agenda/lib/agendaTime";
import {isClosedAt} from "@/agenda/lib/openingHours";
import {HealthcenterOpening} from "@/core/interfaces/healthcenter/Healthcenter";
import {appointmentDescription, DEFAULT_APPOINTMENT_TITLE} from "@/agenda/lib/appointmentLabel";
import {DEFAULT_APPOINTMENT_COLOR, PAST_APPOINTMENT_MESSAGE, START_STEP_MINUTES} from "@/agenda/constants";
import {emptyPatientSmsChoice, isPatientSmsChoiceValid, PatientSmsChoice, phoneLabel} from "@/agenda/lib/patientSms";
import AgendaPatientSmsOption from "@/agenda/components/AgendaPatientSmsOption.vue";

const props = defineProps<{
    /** The appointment to edit; null for a creation. */
    appointment?: AgendaAppointment | null;
    /** Slot start pre-filling the creation form. */
    initialStart?: Date | null;
    /** Collaborator of the clicked column, pre-selected on creation. */
    initialOperatorAccessId?: number | null;
    /**
     * Patient pre-filled on creation, when the pharmacist comes from that
     * patient's file rather than from a slot. Still replaceable in the search.
     */
    initialPatient?: SelectedPatient | null;
    /**
     * Opening sheet of the pharmacy, to warn about a start while it is
     * closed. A warning only: the counter may well book outside its hours.
     */
    opening?: HealthcenterOpening | null;
    loading?: boolean;
}>();

const emits = defineEmits<{
    /** `sms` is set only on an edit that moves the appointment of the same patient. */
    (e: 'submit', request: AppointmentCreateRequest, sms: PatientSmsChoice | null): void;
}>();

const open = defineModel<boolean>('open', {required: true});

const {data: types} = useAppointmentTypes();

/**
 * The whole team: an appointment designates its operator by access point, so
 * members without an Apodis user account are assignable like the others.
 */
const {operators} = useAgendaOperators();

/**
 * The team as the select offers it, in its order for the chosen type: those
 * habilitated first and marked, everybody else still assignable.
 */
const {choices: operatorChoices} = useOperatorChoices(() => ({appointmentTypeId: appointmentTypeId.value}));

const selectedOperator = computed(() => {
    return operators.value.find((operator) => operator.accessId === operatorAccessId.value) ?? null;
});

/**
 * Whether the edit designates another operator than the one stored.
 *
 * Compared with the stored column rather than with what the form resolved on
 * opening: an older appointment whose operator is still in the team resolves
 * to their access point, and sending it backfills the column. An operator who
 * left resolves to nothing — or to an access point that no longer exists —
 * and sending that would strip them, or fail the server's validation. An
 * explicit « Non attribué » pick always counts as a change.
 */
const operatorChanged = computed(() => {
    return !!props.appointment
        && (operatorCleared.value || operatorAccessId.value !== props.appointment.operator_access_point_id);
});

/**
 * The operator of an edited appointment who is no longer in the team, still
 * named on the appointment until the pharmacist picks somebody else. The
 * access point of a removed member is deleted, so only the user account may
 * still carry the name.
 */
const formerOperatorLabel = computed(() => {
    const appointment = props.appointment;

    if (!appointment || operatorChanged.value || selectedOperator.value || isUnassigned(appointment)) {
        return null;
    }

    const name = appointment.operator_access_point?.name ?? appointment.operator?.name;

    return name ? `Ancien membre : ${name}` : 'Ancien membre';
});

/**
 * reka-ui refuses an item with an empty value, so « Non attribué » carries a
 * sentinel the select handler turns back into null.
 */
const UNASSIGNED_VALUE = 'none';

/**
 * The form's fields as the design system draws them: white over the tinted
 * slot block, rounder, with a violet focus. Kept to this form, the shared
 * inputs serve every other screen.
 */
const FIELD_CLASS = 'rounded-lg border-solid bg-white text-sm focus-visible:border-primary focus-visible:ring-primary';

/**
 * The shared input is a flex box, which packs Chromium's calendar and clock
 * indicator right after the value: a block input sends it back to the edge.
 */
const PICKER_CLASS = 'block [&::-webkit-calendar-picker-indicator]:ml-auto [&::-webkit-calendar-picker-indicator]:opacity-60';

/**
 * Left without value while a former member is shown: « Non attribué » must
 * stay pickable to take them off the appointment.
 */
const operatorSelectValue = computed(() => {
    if (operatorAccessId.value !== null) {
        return String(operatorAccessId.value);
    }

    return formerOperatorLabel.value ? undefined : UNASSIGNED_VALUE;
});

/** Map the picked item back onto the operator, the sentinel onto null. */
const selectOperator = (value: unknown) => {
    operatorCleared.value = value === UNASSIGNED_VALUE;
    operatorAccessId.value = value && !operatorCleared.value ? Number(value) : null;
};

const appointmentTypeId = ref<number | null>(null);
const duration = ref(30);
const startDate = ref('');
const startTime = ref('');
const patient = ref<SelectedPatient | null>(null);
const operatorAccessId = ref<number | null>(null);
/**
 * Whether the pharmacist picked « Non attribué ». Told apart from an operator
 * that resolved to nothing: an older appointment whose operator left opens on
 * null too, and only an explicit pick may take that operator off.
 */
const operatorCleared = ref(false);
const title = ref('');
const description = ref('');
// Snapshots taken when the form opens, to send only what an edit changed.
let initialTitle = '';
let initialDescription = '';
const location = ref('');
const memo = ref('');
const sms = ref<PatientSmsChoice>(emptyPatientSmsChoice());

const isEditing = computed(() => !!props.appointment);

/**
 * Falls back on the appointment's own type: a template the pharmacy hid since
 * the booking is no longer offered, yet still the type of this appointment.
 */
const selectedType = computed(() => {
    const ownType = props.appointment?.appointment_type;

    return types.value?.find((type) => type.id === appointmentTypeId.value)
        ?? (ownType && ownType.id === appointmentTypeId.value ? ownType : null);
});

/** Whether the description reads the selected type's default text. */
const descriptionIsTypeDefault = computed(() => {
    const current = description.value.trim();

    return !!current && current === selectedType.value?.default_description?.trim();
});

/**
 * Reset the form each time the modal opens: from the edited appointment, or
 * from the clicked slot (defaulting to the next round half-hour).
 */
watch(open, (isOpen) => {
    if (!isOpen) {
        return;
    }

    const appointment = props.appointment;
    const start = appointment
        ? parseAgendaDate(appointment.start_at)
        : parseAgendaDate(props.initialStart ?? defaultSlotForDay(new Date()));

    startDate.value = start.format('YYYY-MM-DD');
    startTime.value = start.format('HH:mm');
    appointmentTypeId.value = appointment?.appointment_type?.id ?? null;
    duration.value = appointment
        ? parseAgendaDate(appointment.end_at).diff(appointment.start_at, 'minute')
        : selectedType.value?.duration ?? 30;
    // An edit keeps the appointment's own patient, none included: falling
    // back on the open file's patient would bind it on save.
    if (appointment) {
        patient.value = appointment.customer
            ? {id: appointment.customer.id, name: appointment.customer.name ?? 'Patient'}
            : null;
    } else {
        patient.value = props.initialPatient ?? null;
    }
    // Resolved, not read straight off the column: an older appointment carries
    // only its operator's user id, and opening the select empty would strip
    // that operator on save.
    operatorAccessId.value = appointment
        ? accessIdForAppointment(appointment, operators.value)
        : props.initialOperatorAccessId ?? null;
    operatorCleared.value = false;
    title.value = initialTitle = appointment?.title ?? '';
    description.value = initialDescription = appointmentDescription(appointment) ?? '';
    location.value = appointment?.location ?? '';
    memo.value = appointment?.pharmacist_memo ?? '';
    sms.value = emptyPatientSmsChoice();
});

/** Whether the title is still the one the type gave, rather than one typed by hand. */
const titleFollowsType = () => {
    const current = title.value.trim();

    return !current || current === DEFAULT_APPOINTMENT_TITLE || current === selectedType.value?.name;
};

/**
 * Whether the description is still the one the type gave. A program's text or
 * one typed by hand is neither, and is never replaced.
 */
const descriptionFollowsType = () => !description.value.trim() || descriptionIsTypeDefault.value;

/**
 * Selecting a type applies its default duration and, until the pharmacist
 * typed their own, its name as the title and its default description; all
 * stay editable afterwards.
 */
const handleTypeChange = (value: unknown) => {
    const followsType = titleFollowsType();
    const descriptionFollows = descriptionFollowsType();

    appointmentTypeId.value = value ? Number(value) : null;

    if (selectedType.value) {
        duration.value = selectedType.value.duration;

        if (followsType) {
            title.value = selectedType.value.name;
        }

        if (descriptionFollows) {
            description.value = selectedType.value.default_description ?? '';
        }
    }
};

/**
 * An appointment whose type was soft-deleted comes back without a type: the
 * edition must stay possible (the type is only mandatory on creation, where
 * it drives the title and duration).
 */
const missesType = computed(() => isEditing.value && !appointmentTypeId.value);

const router = useRouter();

/**
 * A patient's next appointment is best planned as their whole health journey,
 * the choice the pharmacy is meant to push: the form steps aside for the
 * screen that proposes it, the patient already picked there.
 */
const openHealthJourney = async () => {
    if (!patient.value) {
        return;
    }

    const customerId = patient.value.id;

    open.value = false;
    await router.push({name: 'agenda.health-journey', query: {customerId}});
};

const now = useNow({interval: 1000});

const chosenStart = computed(() => {
    return startDate.value && startTime.value
        ? parseAgendaDate(new Date(`${startDate.value}T${startTime.value}`))
        : null;
});

/**
 * A past appointment stays editable on its other fields: the past rule only
 * bites once its date or time is touched, as on the server.
 */
const startsInPast = computed(() => {
    if (!chosenStart.value) {
        return false;
    }

    if (props.appointment && parseAgendaDate(props.appointment.start_at).isSame(chosenStart.value, 'minute')) {
        return false;
    }

    return isPastStart(chosenStart.value.toDate(), now.value);
});

/**
 * The chosen start falls while the pharmacy is closed by its sheet. Said,
 * not enforced: the pharmacist may be booking a home visit or an opening on
 * appointment, and the past rule already covers what the server refuses.
 * Like that rule, it stays quiet on an edit that leaves the start alone: the
 * slot was chosen knowingly once.
 */
const startsWhileClosed = computed(() => {
    if (!chosenStart.value) {
        return false;
    }

    if (props.appointment && parseAgendaDate(props.appointment.start_at).isSame(chosenStart.value, 'minute')) {
        return false;
    }

    return isClosedAt(props.opening, chosenStart.value);
});

/** The start in the format sent to the API, so the preview names the same time. */
const startAt = computed(() => chosenStart.value?.format() ?? null);

/**
 * A creation can confirm the appointment to its patient once it has one. An
 * edit only texts a new time, and only to the patient the appointment already
 * had: removing or replacing the patient moves nothing for anybody.
 */
const offersSms = computed(() => {
    if (!props.appointment) {
        return !!patient.value && !!chosenStart.value;
    }

    return !!props.appointment.customer
        && patient.value?.id === props.appointment.customer.id
        && !!chosenStart.value
        && !chosenStart.value.isSame(parseAgendaDate(props.appointment.start_at), 'minute');
});

/**
 * The choice was made for one patient: another one starts unticked, with the
 * mobile of their own file.
 */
watch(() => patient.value?.id, () => {
    if (!isEditing.value) {
        sms.value = emptyPatientSmsChoice();
    }
});

/**
 * Lower bounds of the date and time inputs. Left out for an appointment
 * already in the past, whose untouched date would otherwise fail the
 * browser's own validation and block the submit.
 */
const minDate = computed(() => {
    if (props.appointment && isPastStart(props.appointment.start_at, now.value)) {
        return undefined;
    }

    return parseAgendaDate(now.value).format('YYYY-MM-DD');
});

/**
 * Aligned on the input's step: the browser counts steps from `min`, so an
 * unaligned bound would turn every round time into a step mismatch.
 */
const minTime = computed(() => {
    if (!minDate.value || startDate.value !== minDate.value) {
        return undefined;
    }

    return earliestStart(now.value).format('HH:mm');
});

const canSubmit = computed(() => {
    return (isEditing.value || !!appointmentTypeId.value)
        && !!chosenStart.value
        && !startsInPast.value
        && (!offersSms.value || isPatientSmsChoiceValid(sms.value))
        && !props.loading;
});

/** The patient's initials, first two words of their name. */
const patientInitials = computed(() => {
    return (patient.value?.name ?? '')
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word[0].toUpperCase())
        .join('');
});

/** Whether the chosen operator is habilitated for the type, marked in the trigger as in the list. */
const selectedOperatorHabilitated = computed(() => {
    return operatorChoices.value.some((choice) => {
        return choice.habilitated && choice.operator.accessId === operatorAccessId.value;
    });
});

/** Whether the title still reads the type's name, so the pharmacist knows where it came from. */
const titleFromType = computed(() => !!selectedType.value && title.value.trim() === selectedType.value.name);

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/** The chosen slot read back in words, its end computed from the duration. */
const slotSummary = computed(() => {
    if (!chosenStart.value) {
        return null;
    }

    return {
        day: capitalize(chosenStart.value.format('dddd D MMMM YYYY')),
        start: chosenStart.value.format('HH:mm'),
        end: chosenStart.value.add(duration.value, 'minute').format('HH:mm')
    };
});

/** The slot an edited appointment held, shown struck through once the form moves it. */
const previousSlot = computed(() => {
    const appointment = props.appointment;

    if (!appointment || !chosenStart.value) {
        return null;
    }

    const start = parseAgendaDate(appointment.start_at);
    const end = parseAgendaDate(appointment.end_at);

    if (start.isSame(chosenStart.value, 'minute') && end.diff(start, 'minute') === duration.value) {
        return null;
    }

    return `${capitalize(start.format('dddd D MMMM'))} · ${start.format('HH:mm')} → ${end.format('HH:mm')}`;
});

const handleSubmit = () => {
    if (!canSubmit.value || !chosenStart.value) {
        return;
    }

    const customerId = patient.value?.id ?? null;
    const titleValue = title.value.trim() || null;
    const descriptionValue = description.value.trim() || null;

    /*
     * A creation leaves its empty fields out and an edit only sends the title
     * and description that changed: an absent key lets the server fall back
     * to the type, where an older server refuses a null on creation and fails
     * writing it on edit.
     */
    const sendsTitle = isEditing.value ? title.value !== initialTitle : titleValue !== null;
    const sendsDescription = isEditing.value ? description.value !== initialDescription : descriptionValue !== null;

    emits('submit', {
        ...(isEditing.value || customerId !== null ? {customer_id: customerId} : {}),
        ...(sendsTitle ? {title: titleValue} : {}),
        ...(sendsDescription ? {description: descriptionValue} : {}),
        // Omitted rather than sent as null: the appointment keeps the FK to its
        // (possibly soft-deleted) type instead of losing that history.
        ...(appointmentTypeId.value ? {appointment_type_id: appointmentTypeId.value} : {}),
        start_at: chosenStart.value.format(),
        duration: duration.value,
        // An edit leaves the operator out unless it changed: see operatorChanged.
        ...(!isEditing.value || operatorChanged.value ? {operator_access_point_id: operatorAccessId.value} : {}),
        location: location.value.trim() || null,
        pharmacist_memo: memo.value.trim() || null
    }, offersSms.value ? sms.value : null);
};
</script>

<template>
  <Dialog v-model:open="open">
    <DialogContent class="flex max-h-[90vh] max-w-[680px] flex-col gap-0 overflow-hidden p-0">
      <DialogHeader class="border-b border-solid border-slate-200 px-7 pb-5 pt-6 text-left">
        <DialogTitle class="text-lg text-primary">
          {{ isEditing ? 'Modifier le rendez-vous' : 'Nouveau rendez-vous' }}
        </DialogTitle>
        <DialogDescription class="text-xs">
          <template v-if="isEditing">
            Modifiez les informations du rendez-vous.
          </template>
          <template v-else>
            Planifiez un rendez-vous pour un patient ou pour l'équipe.
          </template>
        </DialogDescription>
      </DialogHeader>
      <form class="flex min-h-0 flex-1 flex-col" @submit.prevent="handleSubmit">
        <div class="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-7 py-6">
          <div class="flex flex-col gap-1.5">
            <Label>
              Patient
              <span v-if="!isEditing" class="font-normal text-gray-500">(facultatif)</span>
            </Label>
            <div
              v-if="patient"
              class="flex items-center gap-3 rounded-lg border border-solid border-slate-200 px-3 py-2.5"
              :class="isEditing ? 'bg-gray-light' : 'bg-white'"
            >
              <span
                class="flex size-9 shrink-0 items-center justify-center rounded-full text-[13px] font-semibold text-primary"
                :class="isEditing ? 'bg-white' : 'bg-lightPurple-lightest'"
                aria-hidden="true"
              >
                {{ patientInitials }}
              </span>
              <span class="flex min-w-0 flex-1 flex-col">
                <span class="truncate text-sm font-medium">{{ patient.name }}</span>
                <span v-if="patient.phone" class="flex items-center gap-1.5 text-xs tabular-nums text-gray-500">
                  <Smartphone class="size-4 shrink-0" />
                  {{ phoneLabel(patient.phone) }}
                </span>
              </span>
              <span v-if="isEditing" class="flex shrink-0 items-center gap-1.5 text-xs text-gray-500">
                <Lock class="size-3.5" />
                Non modifiable
              </span>
              <Button
                v-else
                type="button"
                variant="ghost"
                size="icon"
                class="size-8 shrink-0 text-gray-500"
                aria-label="Retirer le patient"
                @click="patient = null"
              >
                <X class="size-4" />
              </Button>
            </div>
            <AgendaPatientSearch v-else v-model="patient" :disabled="isEditing" />
          </div>

          <div
            v-if="patient && !isEditing"
            class="-mt-2 flex items-center gap-3 rounded-lg border-[1.5px] border-solid border-primary bg-primary/5 p-3"
          >
            <span class="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary text-white" aria-hidden="true">
              <Sparkles class="size-[18px]" />
            </span>
            <span class="flex min-w-0 flex-1 flex-col gap-0.5">
              <span class="text-sm font-semibold text-primary">Automatiser le parcours officinal de santé</span>
              <span class="text-xs leading-relaxed text-gray-600">
                Une série de rendez-vous proposée selon le profil santé du patient : zona, grippe, bilan, entretien.
              </span>
            </span>
            <Button
              type="button"
              size="sm"
              class="shrink-0 bg-primary text-white hover:bg-primary/90"
              @click="openHealthJourney"
            >
              Générer le parcours
            </Button>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div class="flex min-w-0 flex-col gap-1.5">
              <Label>
                Type de rendez-vous
                <span v-if="!isEditing" class="text-red-light">*</span>
              </Label>
              <Select
                :model-value="appointmentTypeId ? String(appointmentTypeId) : undefined"
                @update:model-value="handleTypeChange"
              >
                <!--
                  reka's SelectValue cannot resolve the label of a value set
                  before the options were ever mounted (the content lives in a
                  closed portal), so the selected label is rendered explicitly.
                -->
                <SelectTrigger :class="[FIELD_CLASS, 'focus:border-primary focus:ring-primary']">
                  <span v-if="selectedType" class="flex min-w-0 flex-1 items-center gap-2">
                    <span
                      class="size-2.5 shrink-0 rounded-full"
                      :style="{backgroundColor: selectedType.color ?? DEFAULT_APPOINTMENT_COLOR}"
                    />
                    <span class="truncate">{{ selectedType.name }}</span>
                    <span class="ml-auto pr-1 text-xs tabular-nums text-gray-500">{{ selectedType.duration }} min</span>
                  </span>
                  <SelectValue v-else placeholder="Choisir un type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    v-for="type in types ?? []"
                    :key="type.id"
                    :value="String(type.id)"
                  >
                    <span class="flex items-center gap-2">
                      <span
                        class="size-2.5 shrink-0 rounded-full"
                        :style="{backgroundColor: type.color ?? DEFAULT_APPOINTMENT_COLOR}"
                      />
                      {{ type.name }}
                      <span class="text-xs tabular-nums text-gray-500">{{ type.duration }} min</span>
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div class="flex min-w-0 flex-col gap-1.5">
              <Label>Intervenant</Label>
              <Select
                :model-value="operatorSelectValue"
                @update:model-value="selectOperator"
              >
                <SelectTrigger :class="[FIELD_CLASS, 'focus:border-primary focus:ring-primary']">
                  <span v-if="selectedOperator" class="flex min-w-0 flex-1 items-center gap-2">
                    <span class="truncate">{{ selectedOperator.name }}</span>
                    <HabilitatedBadge v-if="selectedOperatorHabilitated" class="ml-auto mr-1" />
                  </span>
                  <span v-else-if="formerOperatorLabel" class="truncate text-gray-500">{{ formerOperatorLabel }}</span>
                  <SelectValue v-else placeholder="Non attribué" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem :value="UNASSIGNED_VALUE">
                    Non attribué
                  </SelectItem>
                  <SelectItem
                    v-for="choice in operatorChoices"
                    :key="choice.operator.key"
                    :value="String(choice.operator.accessId)"
                  >
                    <span class="flex items-center gap-2">
                      {{ choice.operator.name }}
                      <HabilitatedBadge v-if="choice.habilitated" />
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div
            v-if="missesType"
            class="-mt-2 flex items-start gap-2.5 rounded-lg bg-orange-light px-3 py-2.5 text-xs text-gray-dark"
          >
            <TriangleAlert class="mt-px size-4 shrink-0" />
            <p>
              Ce rendez-vous n'a pas de type actif — il a peut-être été supprimé de la
              configuration. Vous pouvez enregistrer sans y toucher, ou en choisir un nouveau.
            </p>
          </div>

          <div class="flex flex-col gap-1.5">
            <div class="flex items-center justify-between gap-2">
              <Label for="agenda-form-title">Titre</Label>
              <span v-if="titleFromType" class="text-xs text-gray-500">Repris du type, modifiable</span>
            </div>
            <Input id="agenda-form-title" v-model="title" placeholder="Ex : Visite labo Pierre Fabre" maxlength="255" :class="FIELD_CLASS" />
          </div>

          <div class="flex flex-col gap-3.5 rounded-lg bg-primary-lightest p-4">
            <div class="grid grid-cols-[minmax(0,1fr)_120px_160px] items-end gap-3">
              <div class="flex flex-col gap-1.5">
                <Label for="agenda-form-date">Date</Label>
                <Input id="agenda-form-date" v-model="startDate" type="date" :min="minDate" :class="[FIELD_CLASS, PICKER_CLASS]" />
              </div>
              <div class="flex flex-col gap-1.5">
                <Label for="agenda-form-time">Heure</Label>
                <Input
                  id="agenda-form-time"
                  v-model="startTime"
                  type="time"
                  :min="minTime"
                  :step="START_STEP_MINUTES * 60"
                  :class="[FIELD_CLASS, PICKER_CLASS]"
                />
              </div>
              <div class="flex flex-col gap-1.5">
                <Label>Durée</Label>
                <div class="flex h-9 items-center justify-center rounded-lg border border-solid border-slate-200 bg-white shadow-sm">
                  <DurationStepper v-model="duration" />
                </div>
              </div>
            </div>
            <p v-if="startsInPast" class="-mt-1.5 text-xs text-red-600">
              {{ PAST_APPOINTMENT_MESSAGE }}
            </p>
            <p v-else-if="startsWhileClosed" class="-mt-1.5 flex items-center gap-1.5 text-xs text-gray-dark">
              <TriangleAlert class="size-3.5 shrink-0 text-orange" />
              La pharmacie est fermée sur ce créneau. Vous pouvez tout de même enregistrer le rendez-vous.
            </p>
            <p
              v-if="slotSummary && previousSlot"
              class="-mt-1 flex flex-wrap items-center justify-end gap-x-2 gap-y-0.5 text-xs tabular-nums"
            >
              <span class="text-gray-500 line-through">{{ previousSlot }}</span>
              <ArrowRight class="size-3.5 text-gray-500" />
              <span class="font-semibold text-primary">
                {{ slotSummary.day }} · {{ slotSummary.start }} → {{ slotSummary.end }}
              </span>
            </p>
            <p v-else-if="slotSummary" class="-mt-1 text-right text-xs text-gray-500">
              Fin à <span class="font-semibold tabular-nums text-primary">{{ slotSummary.end }}</span>
            </p>
          </div>

          <AgendaPatientSmsOption
            v-if="offersSms && appointment"
            v-model="sms"
            bare
            :appointment-id="appointment.id"
            event="rescheduled"
            :start-at="startAt"
          />
          <!-- Keyed on the patient: the mobile of the file is prefilled once per instance. -->
          <AgendaPatientSmsOption
            v-else-if="offersSms && patient"
            :key="patient.id"
            v-model="sms"
            bare
            :customer-id="patient.id"
            event="created"
            :start-at="startAt"
          />

          <div class="flex flex-col gap-1.5">
            <Label for="agenda-form-location">Lieu</Label>
            <Input id="agenda-form-location" v-model="location" placeholder="Ex : Salle de confidentialité" maxlength="255" :class="FIELD_CLASS" />
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div class="flex flex-col gap-1.5">
              <div class="flex items-center justify-between gap-2">
                <Label for="agenda-form-description">Description</Label>
                <span v-if="descriptionIsTypeDefault" class="text-xs text-gray-500">Repris du type, modifiable</span>
              </div>
              <Textarea
                id="agenda-form-description"
                v-model="description"
                :class="[FIELD_CLASS, 'flex-1']"
                placeholder="Ex : Présentation de la nouvelle gamme solaire"
                maxlength="255"
              />
            </div>
            <div class="flex flex-col gap-2 rounded-lg bg-lightPurple-lightest px-3 pb-3 pt-2.5">
              <div class="flex items-center gap-1.5 text-darkPurple">
                <Lock class="size-3.5 shrink-0" />
                <Label for="agenda-form-memo">Mémo pharmacien</Label>
                <span class="ml-auto text-[11px]">Non visible du patient</span>
              </div>
              <Textarea id="agenda-form-memo" v-model="memo" :class="FIELD_CLASS" placeholder="Note interne à l'équipe" />
            </div>
          </div>
        </div>

        <DialogFooter class="items-center gap-3 border-t border-solid border-slate-200 px-7 py-4 sm:justify-between">
          <p class="text-xs text-gray-500">
            <template v-if="!isEditing && !appointmentTypeId">
              Choisissez un type pour créer le rendez-vous.
            </template>
            <template v-else-if="!isEditing">
              <span class="text-red-light">*</span> Champ obligatoire
            </template>
          </p>
          <div class="flex gap-2">
            <Button type="button" variant="outline" @click="open = false">
              Annuler
            </Button>
            <Button type="submit" class="bg-primary" :disabled="!canSubmit">
              {{ isEditing ? 'Enregistrer' : 'Créer le rendez-vous' }}
            </Button>
          </div>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
</template>
